import React, { useRef, useState } from "react";
import type * as types from "../PhoenixFormsV4";
import type * as controls from "../PhoenixFormsV4.Controls";
import type * as appData from "../PhoenixFormsV4.AppData";
import PNXLabel from "./PNXLabel";
import { usePhoenixForms } from "../PhoenixFormsProvider";
import { FormFileControlData, FormFileResourceMetadata } from "../PhoenixFormsV4.Models";
import { usePhoenixFormsDialog } from "./usePhoenixFormsDialog";
import { phoenixDownloadFile } from "tools/fileDownloadTools";

const restrictedExtensions = [
  "exe",
  "com",
  "scr",
  "dll",
  "ocx",
  "bat",
  "cmd",
  "vbs",
  "js",
  "ps1",
  "pif",
  "reg",
  "sh",
  "msi",
  "cpl",
  "jar",
];

interface Props {
  controlProps: controls.FormControl;
  proxy: types.PhoenixFormsProxy;
  webRuntimeData: appData.WebRuntimeAppData;
  readonly?: boolean;
};

const PhoenixFileControl = ({
  controlProps,
  proxy,
  webRuntimeData,
  readonly,
} : Props) => {

  const { isPreview } = usePhoenixForms();
  const dialog  = usePhoenixFormsDialog();
  const uploader = useRef<HTMLInputElement>(null);
  const [fileData, setFileData] = useState<FormFileControlData>(proxy.requestFormFileData(controlProps.id));

  const required = controlProps.required && fileData.files.length === 0;

  const refreshFileData = () => {
    setFileData(proxy.requestFormFileData(controlProps.id));
  };

  const onUploadClick = () => {
    uploader.current.value = "";
    uploader.current.click();
  };

  const uploaderOnChange = () => {
    if (!uploader.current.files.length) return;
    const file = uploader.current.files[0];

    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (restrictedExtensions.includes(fileExtension)) {
      dialog.showError(`The file with extension ".${fileExtension}" is not allowed for upload. Please choose a different file.`);
      return;
    }

    dialog.showLoader("Uploading File...");

    const xmlhttp = new XMLHttpRequest();
    xmlhttp.open("POST", `${webRuntimeData.host}/services/forms4/${webRuntimeData.fileId}/phoenix-editor/${webRuntimeData.sessionKey}/formFiles?fileName=${encodeURIComponent(file.name)}`, true);

    let complete = false;
    xmlhttp.onreadystatechange = () => {
      if (xmlhttp.readyState === 4 && !complete) {
          complete = true;

          if (xmlhttp.status !== 0) {
            dialog.hide();

            if (xmlhttp.status === 200) {
              const meta = JSON.parse(xmlhttp.responseText);
              proxy.fileAdd(controlProps.id, meta);
              refreshFileData();
            }
            else {
              dialog.showError("Error uploading file");
            }
          }
      }
    };

    xmlhttp.setRequestHeader("Content-Type", "application/octet-stream");
    xmlhttp.send(file);
  };

  const onDownloadClick = (file: FormFileResourceMetadata) => {
    dialog.showLoader(`Downloading ${file.name}...`);
    phoenixDownloadFile(`/services/forms4/${webRuntimeData.fileId}/phoenix-editor/${webRuntimeData.sessionKey}/formFiles/${file.id}`, {
      completeCallback: () => {
        dialog.hide();
      },
      fileNameOverride: file.name
    });
  };

  const onDeleteClick = (file: FormFileResourceMetadata) => {
    dialog.confirmDelete(file.name, () => {
      proxy.fileDelete(controlProps.id, file.id);
      refreshFileData();
    });
  };
  
  return <div>
    <div className="file-header">
      <PNXLabel helpTopicId={!readonly && controlProps.topicId}>{controlProps.label}</PNXLabel>
      <div className="file-options" onClick={ !isPreview && !readonly ? onUploadClick : undefined }></div>
      <input 
        type="file" 
        className="uploader" 
        ref={uploader}
        onChange={uploaderOnChange}
      />
    </div>
    {required && <div className="phoenix-validation">{"[ * Required ]"}</div>}
    {fileData.files.length > 0 && 
    <div className="file-list">
      {fileData.files.map(file => (
        <div key={file.id} className="file-item">
          <div className="file-item-name" onClick={() => onDownloadClick(file)}>{file.name} :: {(Math.round((file.fileSize / 1024) * 100) / 100).toLocaleString("en-US")} KB</div>
          {!readonly && <div className="file-item-options" onClick={() => onDeleteClick(file)}><i className="md-icon material-icons">delete</i></div>}
        </div>
      ))}
    </div>}
  </div>;
};

export default PhoenixFileControl;